/**
 * Color By VMG (VMG % of target) — shared by AdvancedScatter and the ScatterTimeseries strip above it,
 * so the two can never disagree on which value a row carries or where it lands on the ramp.
 * VMG % is a ratio, so RaceSight's kph storage does not matter here.
 */
import { defaultChannelsStore } from "../store/defaultChannelsStore";
import { resolveDataField } from "./colorScale";

function avgNumericFromRow(row: Record<string, unknown>, channelName: string): number | undefined {
    const v =
        resolveDataField(row, channelName) ??
        resolveDataField(row, `${channelName}_avg`) ??
        resolveDataField(row, `${channelName.toLowerCase()}_avg`);
    if (v === undefined || v === null || v === '') {return undefined;}
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
}

/** Average VMG % of target: the class channel (Vmg_perc), else the events_aggregate `Vmg_percent`. */
export function vmgPercFromRow(row: Record<string, unknown> | undefined): number | undefined {
    if (!row) {return undefined;}
    for (const name of [defaultChannelsStore.vmgPercName(), 'Vmg_percent']) {
        if (!name) {continue;}
        const n = avgNumericFromRow(row, name);
        if (n !== undefined) {return n;}
    }
    return undefined;
}

/**
 * Place each item on this chart's VMG % range (2nd–98th percentile, so a stray point does not wash
 * the rest out) as a 0..1 fraction. Items without a VMG % are left out of the map (drawn grey).
 */
export function vmgFractions<T extends object>(
    items: readonly T[],
    vmgOf: (item: T) => number | undefined
): WeakMap<T, number> {
    const out = new WeakMap<T, number>();
    const withVmg: Array<{ item: T; v: number }> = [];
    for (const item of items) {
        if (!item || typeof item !== 'object') {continue;}
        const v = vmgOf(item);
        if (v !== undefined && Number.isFinite(v)) {withVmg.push({ item, v });}
    }
    if (withVmg.length === 0) {return out;}
    const sorted = withVmg.map((w) => w.v).sort((a, b) => a - b);
    const quantile = (p: number): number => {
        const i = (sorted.length - 1) * p;
        const lo = Math.floor(i);
        const hi = Math.ceil(i);
        return sorted[lo]! + (sorted[hi]! - sorted[lo]!) * (i - lo);
    };
    const lo = quantile(0.02);
    const span = quantile(0.98) - lo;
    for (const { item, v } of withVmg) {
        const fraction = span > 0 ? (v - lo) / span : 0.5;
        out.set(item, Math.min(1, Math.max(0, fraction)));
    }
    return out;
}
